import { AdminRepresentatives } from '@platform/client';
import {
  AccountTypeEnum,
  AdminRepresentative,
  OrganizationApplicationStatusEnum,
} from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo, useState } from 'react';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { OrganizationProfileLink } from '../../components/ProfileLinks';
import { UserEmailCell } from '../../components/UserEmailCell';
import { VerifyEmailModal } from '../professionals/VerifyEmailModal';
import { keepPreviousData } from '@tanstack/react-query';
import { ResetUserModal } from '../../components/ResetUserModal';
import { navigate } from 'wouter/use-browser-location';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';

const columnHelper = createColumnHelper<AdminRepresentative>();

export const RepresentativesAdmin = () => {
  return (
    <AdminListPageWrapper title={'Representatives'}>
      <RepresentativesAdminContent />
    </AdminListPageWrapper>
  );
};

type ResetOrgUserModalData = {
  userId: number;
  currentAccountType: AccountTypeEnum;
  currentAppStatus: OrganizationApplicationStatusEnum | null;
};

const RepresentativesAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { data, isSuccess, isLoading, isError } = AdminRepresentatives.useGetRepresentatives(
    { ...adminQueryOptionsV2 },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const [emailVerifyUserId, setEmailVerifyUserId] = useState<number | undefined>();
  const [resetUserData, setResetUserData] = useState<ResetOrgUserModalData | undefined>();
  const { representatives = [], filteredCount = 0 } = data || {};

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'ID',
          cell: (info) => info.getValue(),
          size: 20,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: (repName) => repName.getValue(),
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('email', {
          header: 'Email',
          cell: ({ row }) => (
            <UserEmailCell
              email={row.original.email}
              emailVerified={row.original.emailVerified}
              userId={row.original.id}
              setEmailVerifyUserId={setEmailVerifyUserId}
            />
          ),

          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organizationName', {
          header: 'Organization',
          size: 200,
          cell: ({ row }) => (
            <OrganizationProfileLink
              organizationId={row.original.organizationId}
              organizationName={row.original.organizationName}
            />
          ),
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (info) => <DateCellFromString value={info.getValue()} />,
        }),
        columnHelper.accessor('updatedAt', {
          header: 'Updated At',
          cell: (info) => <DateCellFromString value={info.getValue()} />,
        }),
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          size: 20,
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  type: 'button',
                  text: 'Reset',
                  onPress: () =>
                    setResetUserData({
                      userId: row.original.id,
                      currentAccountType: AccountTypeEnum.organization,
                      currentAppStatus: row.original.organizationApplicationStatus,
                    }),
                },
              ]}
            />
          ),
        }),
      ] as ColumnDef<AdminRepresentative>[],
    [],
  );
  return (
    <>
      <AdminTable
        columns={columns}
        filteredCount={filteredCount}
        data={representatives}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        enableFiltering={true}
        tableActions={[
          {
            text: 'Search',
            callback: () => {
              navigate(`platform${ROUTES.ADMIN}${ADMIN_ROUTES.SEARCH_REPRESENTATIVES}`);
            },
          },
        ]}
      />
      {emailVerifyUserId && (
        <VerifyEmailModal
          userId={emailVerifyUserId}
          onClose={() => setEmailVerifyUserId(undefined)}
        />
      )}
      {resetUserData && (
        <ResetUserModal
          userId={resetUserData.userId}
          currentAccountType={resetUserData.currentAccountType}
          currentAppStatus={resetUserData.currentAppStatus}
          onClose={() => setResetUserData(undefined)}
        />
      )}
    </>
  );
};
